import { isEqual } from 'lodash';
import moment from 'moment';
import React, { Dispatch } from 'react';
import {
    SavedFilters,
    SearchInput,
    SearchInputChangeEvent,
    SearchSection,
} from '~/components/Search';
import {
    ClinicList,
    PCPList,
    SearchContext,
    SearchContextType,
    SearchFilterType,
    SearchValuesType,
    ServicesContext,
    initialSearch,
} from '~/services';
import {
    pcpAssignmentOptions,
    planTypes,
    riskBandLevels,
    riskFactorOptions,
    useServiceSubscribe,
} from '~/util';

export interface SearchProps {
    formVals: SearchValuesType;
    setFormVals: Dispatch<FormValuesEvent>;
    PCP: PCPList;
    setPCP: (PCP: PCPList) => void;
    pcpList: PCPList;
    clinic: ClinicList;
    setClinic: (clinic: ClinicList) => void;
    clearFilter: () => void;
}
export function Search({
    formVals,
    setFormVals,
    clinic,
    setClinic,
    PCP,
    setPCP,
    pcpList,
    clearFilter,
}: SearchProps) {
    const { clinicList, insurancesList } = React.useContext(SearchContext) as SearchContextType;
    const context = React.useContext(ServicesContext);

    const selectFilter = React.useCallback(
        (filter: SearchValuesType['filter']) => {
            if (filter) {
                setClinic(filter?.clinic || null);
                setPCP(filter?.PCP || null);

                // Exclude below filters if feature toggles are disabled
                const resolvedFilter = { ...filter };
                if (!context.user.hasFeatureToggle('lite_plan_filter')) {
                    resolvedFilter.planType = initialSearch.planType;
                }
                if (!context.user.hasFeatureToggle('disenrolled_patients')) {
                    resolvedFilter.disenrolled = initialSearch.disenrolled;
                }
                setFormVals({ target: { id: 'filter', value: resolvedFilter } });
            } else {
                clearFilter();
                setFormVals({ target: { id: 'filter', value: filter } });
            }
        },
        [clearFilter, setClinic, setPCP, setFormVals, context.user]
    );

    async function loadGapOptions(val: string) {
        const results = await context.api.call({
            method: 'get',
            url: `/api/gap-types?search_term=${val}`,
        });
        return results.data.map((x: TODO_TYPEME) => ({
            label: x.name,
            name: x.name,
            description: x.description,
            type: x.type,
            id: x.id,
            value: `${x.type}-${x.id}`,
        }));
    }

    const filterMatch = React.useCallback(
        (filter: SearchFilterType) => {
            // compare clinic and PCP
            if (!isEqual(filter.clinic, clinic) || !isEqual(filter.PCP, PCP)) {
                return false;
            }

            // compare the rest of the search form fields
            return Object.keys(formVals).every((k: keyof SearchValuesType) => {
                if (k === 'filter' || k === 'clinic' || k === 'PCP' || !filter) return true;
                if (typeof filter[k] === 'undefined') {
                    return formVals[k] === initialSearch[k];
                }
                if (typeof filter[k] === 'object') {
                    return isEqual(filter[k], formVals[k]);
                }
                // handle date comparison for different string formats
                if (typeof filter[k] === 'string' && filter[k] && Date.parse(filter[k] as string)) {
                    return moment(filter[k] as string).isSame(moment(formVals[k] as string));
                }

                return filter[k] === formVals[k];
            });
        },
        [formVals, clinic, PCP]
    );

    React.useEffect(() => {
        // clear selected filter when formVals mismatch
        if (formVals.filter && !filterMatch(formVals.filter)) {
            setFormVals({ target: { id: 'filter', value: null } });
        }
    }, [filterMatch, setFormVals, formVals.filter]);

    const compareSavedFilters = React.useCallback(() => {
        // auto select saved filter if formVals match
        if (!formVals.filter) {
            const { savedFilters } = context.user.getSavedSearchData();
            savedFilters.some((filter: SearchFilterType) => {
                const matched = filterMatch(filter);
                if (matched) {
                    selectFilter(filter);
                }
                return matched;
            });
        }
    }, [filterMatch, selectFilter, formVals.filter, context.user]);

    React.useEffect(() => {
        compareSavedFilters();
    }, [compareSavedFilters]);
    useServiceSubscribe(context.user.events.savedSearchDataUpdated, compareSavedFilters);

    return (
        <div className="container">
            <SavedFilters
                formVals={formVals}
                selectFilter={selectFilter}
                clinic={clinic}
                PCP={PCP}
            />
            <hr className="my-3" />
            <div className="row search-scroll-container">
                <div className="col">
                    <form id="patient-search">
                        <div className="container">
                            <SearchSection
                                defaultOpen
                                title="Clinic & PCP"
                                fields={['clinic', 'PCP']}
                                values={{ clinic, PCP }}
                            >
                                <div className="row">
                                    <SearchInput
                                        value={clinic}
                                        label="clinic"
                                        options={clinicList}
                                        handleChange={(e: SearchInputChangeEvent) =>
                                            setClinic(e.target.value as ClinicList)
                                        }
                                    />
                                    <SearchInput
                                        value={PCP}
                                        label="PCP"
                                        options={pcpList}
                                        handleChange={(e: SearchInputChangeEvent) =>
                                            setPCP(e.target.value as PCPList)
                                        }
                                    />
                                </div>
                            </SearchSection>
                            <SearchSection
                                defaultOpen
                                title="Member Demographics"
                                fields={['firstName', 'lastName', 'phone', 'DOB', 'memberID']}
                                values={formVals}
                            >
                                <div className="row">
                                    <SearchInput
                                        value={formVals.firstName}
                                        label="firstName"
                                        handleChange={setFormVals}
                                    />
                                    <SearchInput
                                        value={formVals.lastName}
                                        label="lastName"
                                        handleChange={setFormVals}
                                    />
                                    <SearchInput
                                        value={formVals.phone}
                                        label="phone"
                                        handleChange={setFormVals}
                                    />
                                    <SearchInput
                                        value={formVals.DOB}
                                        label="DOB"
                                        handleChange={setFormVals}
                                    />
                                    <SearchInput
                                        value={formVals.memberID}
                                        label="memberID"
                                        handleChange={setFormVals}
                                    />
                                </div>
                            </SearchSection>
                            <SearchSection
                                defaultOpen={false}
                                title="Advanced"
                                fields={[
                                    ...(context.user.hasFeatureToggle('lite_plan_filter')
                                        ? (['planType'] as const)
                                        : []),
                                    'riskBand',
                                    'riskFactor',
                                    'openGaps',
                                    'GMPI',
                                    'insurance',
                                    ...(context.user.hasFeatureToggle('disenrolled_patients')
                                        ? (['disenrolled'] as const)
                                        : []),
                                ]}
                                values={formVals}
                            >
                                {context.user.hasFeatureToggle('lite_plan_filter') && (
                                    <div className="row">
                                        <SearchInput
                                            value={formVals.planType}
                                            label="planType"
                                            options={planTypes}
                                            handleChange={setFormVals}
                                        />
                                    </div>
                                )}
                                <div className="row">
                                    <SearchInput
                                        value={formVals.riskBand}
                                        label="riskBand"
                                        options={riskBandLevels}
                                        handleChange={setFormVals}
                                    />
                                </div>
                                <div className="row">
                                    <SearchInput
                                        value={formVals.riskFactor}
                                        label="riskFactor"
                                        options={riskFactorOptions}
                                        handleChange={setFormVals}
                                    />
                                </div>
                                <div className="row">
                                    <SearchInput
                                        value={formVals.openGaps}
                                        label="openGaps"
                                        options={loadGapOptions}
                                        handleChange={setFormVals}
                                    />
                                </div>
                                <div className="row">
                                    <SearchInput
                                        value={formVals.GMPI}
                                        label="GMPI"
                                        handleChange={setFormVals}
                                    />
                                </div>
                                <div className="row">
                                    <SearchInput
                                        value={formVals.insurance}
                                        label="insurance"
                                        options={insurancesList}
                                        handleChange={setFormVals}
                                    />
                                </div>
                                {context.user.hasFeatureToggle('disenrolled_patients') && (
                                    <div className="row">
                                        <SearchInput
                                            value={formVals.disenrolled}
                                            label="disenrolled"
                                            options={pcpAssignmentOptions}
                                            handleChange={setFormVals}
                                        />
                                    </div>
                                )}
                            </SearchSection>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export type FormValuesEvent =
    | string
    | {
          target: {
              id: string;
              value: string | SearchValuesType['filter'];
              formVals?: SearchValuesType;
          };
      };
export const formValsReducer = (state: SearchValuesType, e: FormValuesEvent) => {
    if (typeof e === 'string' && e === 'clear') {
        return initialSearch;
    }
    if (typeof e === 'string') return state;
    if (e.target.formVals) {
        return { ...state, ...e.target.formVals };
    }
    if (e.target.id === 'filter' && typeof e.target.value !== 'string') {
        if (!e.target.value) {
            return { ...state, filter: null };
        }
        const { label, ...filterVals } = e.target.value;
        return { ...initialSearch, ...filterVals, filter: e.target.value };
    }
    return { ...state, [e.target.id]: e.target.value };
};
